import React from 'react'
import { Link } from 'react-router'

import { useIsSidebarLinkActive } from '@/components/layouts/DashboardLayout/components/DashboardSidebar/hooks/useIsSidebarLinkActive'
import { Badge } from '@/components/ui/badge'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import useNotyf from '@/hooks/useNotyf'
import { cn } from '@/lib/utils'
import { SidebarLink } from '@/types/misc'

interface Props {
  link: SidebarLink
}

const SidebarLinkItem: React.FC<Props> = ({ link }) => {
  const { open, isMobile, setOpenMobile, state } = useSidebar()
  const notyf = useNotyf()
  const isSidebarLinkActive = useIsSidebarLinkActive()
  const {
    title,
    href,
    icon: Icon,
    activeIcon: ActiveIcon,
    badge,
    badgeVariant,
    isHoverOnlyWhenCollapsed,
    onClick,
    onMouseOver,
    className,
    linkClassName,
    comingSoonText,
    style = {},
  } = link
  const isCollapsed = state === 'collapsed'

  const isActive = isSidebarLinkActive({ title, href })
  const displayIcon = isActive && ActiveIcon ? ActiveIcon : Icon

  const badgeVariantOrDefault =
    badgeVariant ?? (isActive ? 'primary' : 'secondary')

  // The same condition the tooltip itself renders under, so the badge is never
  // moved somewhere nothing will show it.
  const shouldShowBadgeInTooltip = Boolean(
    badge && isHoverOnlyWhenCollapsed && isCollapsed && !isMobile,
  )

  // The tooltip only appears when the sidebar is collapsed, which is exactly when
  // the badge has nowhere sensible to sit against a 56px square.
  const tooltip = shouldShowBadgeInTooltip ? (
    <span className="flex items-center gap-2">
      {title}
      <Badge variant={badgeVariantOrDefault}>{badge}</Badge>
    </span>
  ) : (
    title
  )

  return (
    <SidebarMenuItem className={cn('mb-0', className)} style={style}>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={{ children: tooltip }}
        size="lg"
        className={cn(
          'text-foreground no-underline font-medium! py-1',
          // When collapsed, fill the square list item and center the icon
          'group-data-[collapsible=icon]:w-14!',
          'group-data-[collapsible=icon]:h-14!',
          { 'mx-2': isCollapsed },
          'group-data-[collapsible=icon]:justify-center',
          {
            [`bg-primary/10! text-primary! hover:bg-primary/10!
            hover:text-primary!`]: isActive,
          },
        )}
      >
        <Link
          to={href}
          className={cn('flex items-center gap-3 px-1', linkClassName)}
          onMouseOver={(event) => onMouseOver?.(event)}
          onClick={(event) => {
            if (comingSoonText) {
              event.preventDefault()
              notyf.open({
                message: comingSoonText,
                type: 'info',
                duration: 6000,
              })
              return
            }

            if (isMobile) {
              setOpenMobile(false)
            }

            onClick?.(event)
          }}
        >
          {isActive && open && (
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 w-0.75 h-5
                rounded-r-full bg-primary"
            ></div>
          )}

          <span
            className={cn(
              `flex items-center justify-center [&_img]:size-5 [&_svg]:size-5
              group-data-[collapsible=icon]:[&_img]:size-5
              group-data-[collapsible=icon]:[&_svg]:size-5
              text-muted-foreground`,
              { 'text-primary': isActive },
              { 'pl-2': open },
            )}
          >
            {displayIcon}
          </span>

          <span
            className={cn(
              `whitespace-nowrap group-data-[collapsible=icon]:hidden
              text-[13px]`,
              isActive ? 'font-semibold' : 'font-medium',
            )}
          >
            {title}
          </span>

          {badge && !shouldShowBadgeInTooltip && (
            <Badge
              variant={badgeVariantOrDefault}
              className="ml-auto group-data-[collapsible=icon]:absolute
                group-data-[collapsible=icon]:top-1
                group-data-[collapsible=icon]:right-1
                group-data-[collapsible=icon]:ml-0"
            >
              {badge}
            </Badge>
          )}

          {comingSoonText && (
            <Badge
              className="bg-navbar-blue text-body ml-auto
                group-data-[collapsible=icon]:hidden"
            >
              Soon
            </Badge>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

export default SidebarLinkItem
